import { AppShell, Box, Burger, Center, Loader, ScrollArea, Stack, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { Suspense, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { navLinks } from '../../config/navLinks.js';
import { STUDIOS_ROUTE } from '../../domain/routes.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { useTRPC } from '../../utils/trpc.js';
import { Header } from '../Header/Header.js';
import { Logo } from '../Logo/Logo.js';
import { PageHeaderProvider } from '../PageHeader/PageHeaderContext.js';
import { SectionHeading } from '../SectionHeading/SectionHeading.js';
import { AccountBlock } from './AccountBlock.js';
import { NavItem } from './NavItem.js';
import { isActiveNav, navSections, visibleNavLinks } from './navSections.js';
import { useNavVisibility } from './useNavVisibility.js';

/** The sidebar's width above `sm` — `FloatingActionBar` offsets its left edge past it. */
export const NAVBAR_WIDTH = 260;

/**
 * The shell: a sidebar the full height of the window and the page's header beside it. Below
 * `sm` the sidebar is a drawer behind a burger and the layout is the default one, so the drawer
 * opens under the 56px header. There is no environment badge anywhere: the hub is global.
 */
export const AppLayout = () => {
    const isMobile = useIsMobile();
    const [opened, drawer] = useDisclosure(false);
    const location = useLocation();
    const trpc = useTRPC();
    // `staleTime: Infinity` — see `hooks/useWriteAccess.ts`.
    const me = useQuery({ ...trpc.users.me.queryOptions(), staleTime: Infinity });
    const visibility = useNavVisibility();
    const sections = navSections(visibleNavLinks(navLinks, visibility));

    useEffect(() => {
        drawer.close();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const sidebar = (
        <>
            <AppShell.Section
                h={isMobile ? undefined : 80}
                px="md"
                py={isMobile ? 'md' : 0}
                style={{ borderBottom: '1px solid var(--rgs-line)', display: 'flex', alignItems: 'center' }}
            >
                <UnstyledButton component={Link} to={STUDIOS_ROUTE} aria-label="Config Hub — Studios">
                    <Logo />
                </UnstyledButton>
            </AppShell.Section>
            <AppShell.Section grow component={ScrollArea} type="never" px="xs" pb="md">
                {sections.map((group) => (
                    <Box key={group.section}>
                        <SectionHeading variant="group">{group.section}</SectionHeading>
                        <Stack gap={2}>
                            {group.links.map((link) => (
                                <NavItem
                                    key={link.to}
                                    to={link.to}
                                    label={link.label}
                                    icon={link.icon}
                                    active={isActiveNav(link.to, location.pathname)}
                                />
                            ))}
                        </Stack>
                    </Box>
                ))}
            </AppShell.Section>
            <AppShell.Section style={{ borderTop: '1px solid var(--rgs-line)' }}>
                <AccountBlock name={me.data?.name} email={me.data?.email} />
            </AppShell.Section>
        </>
    );

    return (
        <PageHeaderProvider>
            <AppShell
                layout={isMobile ? 'default' : 'alt'}
                header={{ height: { base: 56, sm: 80 } }}
                navbar={{ width: NAVBAR_WIDTH, breakpoint: 'sm', collapsed: { mobile: !opened, desktop: false } }}
                padding={0}
            >
                <AppShell.Header withBorder={false} style={{ background: 'var(--rgs-bg)' }}>
                    <Header
                        burger={
                            isMobile && (
                                <Burger
                                    opened={opened}
                                    onClick={drawer.toggle}
                                    size="sm"
                                    aria-label={opened ? 'Close navigation' : 'Open navigation'}
                                />
                            )
                        }
                    />
                </AppShell.Header>
                <AppShell.Navbar style={{ background: 'var(--rgs-surface)', borderColor: 'var(--rgs-line)' }}>
                    {sidebar}
                </AppShell.Navbar>
                <AppShell.Main bg="var(--rgs-bg)">
                    {/* The routed page's own Suspense boundary: the shell (sidebar, header) stays
                        mounted while a lazy page chunk (an editor, Users, Audit, Environments)
                        loads — only the content area shows the fallback. */}
                    <Suspense
                        fallback={
                            <Center h="100%" mih={400}>
                                <Loader />
                            </Center>
                        }
                    >
                        <Outlet />
                    </Suspense>
                </AppShell.Main>
            </AppShell>
        </PageHeaderProvider>
    );
};
